const DEFAULT_MESSAGE = "요청을 처리하지 못했습니다. 잠시 후 다시 시도해 주세요.";

const TECHNICAL_MESSAGE_PATTERN =
  /failed to fetch|networkerror|load failed|fetch failed|http\s*\d{3}|jwt|invalid token|unexpected token|json|internal server error|service unavailable|gateway|timeout|econn|socket|cors/i;

function readStatus(error, response) {
  const value = Number(
    response?.status ||
    error?.status ||
    error?.statusCode ||
    error?.response?.status ||
    0
  );
  return Number.isFinite(value) ? value : 0;
}

function readRawMessage(error, data) {
  if (typeof error === "string") return error.trim();
  return String(
    data?.message ||
    data?.error_description ||
    data?.error ||
    error?.userMessage ||
    error?.message ||
    ""
  ).trim();
}

function isOffline() {
  return typeof navigator !== "undefined" && navigator.onLine === false;
}

function isKoreanUserMessage(message) {
  return /[가-힣]/.test(message) && !TECHNICAL_MESSAGE_PATTERN.test(message);
}

export class DWorksAppError extends Error {
  constructor(message, options = {}) {
    super(message || DEFAULT_MESSAGE);
    this.name = "DWorksAppError";
    this.code = options.code || "unknown";
    this.status = Number(options.status || 0);
    this.retryable = Boolean(options.retryable);
    this.originalMessage = options.originalMessage || "";
    this.requestId = options.requestId || "";
    if (options.cause) this.cause = options.cause;
  }
}

export function getUserErrorMessage(error, fallback = DEFAULT_MESSAGE) {
  if (error?.userMessage) return error.userMessage;
  if (error instanceof DWorksAppError) return error.message;

  const status = readStatus(error);
  const rawMessage = readRawMessage(error);
  const lower = rawMessage.toLowerCase();

  if (isOffline() || error?.name === "TypeError" || /failed to fetch|networkerror|load failed|fetch failed/.test(lower)) {
    return "인터넷 연결을 확인한 후 다시 시도해 주세요.";
  }
  if (error?.name === "AbortError" || /timeout|timed out/.test(lower)) {
    return "응답이 지연되고 있습니다. 잠시 후 다시 시도해 주세요.";
  }
  if (status === 401 || /jwt expired|invalid token|token.*expired/.test(lower)) {
    return "로그인 시간이 만료되었습니다. 다시 로그인해 주세요.";
  }
  if (status === 403) {
    return "이 작업을 처리할 권한이 없습니다.";
  }
  if (status === 404) {
    return "요청한 정보를 찾을 수 없습니다.";
  }
  if (status === 409 || /duplicate|already exists|already registered/.test(lower)) {
    return "이미 등록된 정보입니다. 입력 내용을 확인해 주세요.";
  }
  if (status === 413) {
    return "첨부 파일의 용량이 너무 큽니다.";
  }
  if (status === 429) {
    return "요청이 많아 처리가 지연되고 있습니다. 잠시 후 다시 시도해 주세요.";
  }
  if (status >= 500) {
    return "서버 연결이 원활하지 않습니다. 잠시 후 다시 시도해 주세요.";
  }
  if (isKoreanUserMessage(rawMessage)) return rawMessage;
  return fallback || DEFAULT_MESSAGE;
}

export function toAppError(error, options = {}) {
  if (error instanceof DWorksAppError) return error;

  const status = readStatus(error, options.response);
  const originalMessage = readRawMessage(error, options.data);
  const message = getUserErrorMessage(
    {
      ...error,
      name: error?.name || "",
      message: originalMessage,
      status
    },
    options.fallback || DEFAULT_MESSAGE
  );

  return new DWorksAppError(message, {
    code: options.code || (status ? `http_${status}` : "request_failed"),
    status,
    retryable:
      options.retryable ??
      (status === 0 || status === 408 || status === 429 || status >= 500),
    originalMessage,
    requestId:
      options.requestId ||
      options.response?.headers?.get?.("x-request-id") ||
      error?.requestId ||
      "",
    cause: error
  });
}

export function createHttpError(response, data, fallback = DEFAULT_MESSAGE) {
  if (response?.status === 503 && data?.code === "DISPATCH_RESULT_UNKNOWN") {
    return new DWorksAppError(
      "일부 전달되었을 수 있어 발송 결과를 확인 중입니다. 중복 전달을 막기 위해 자동 재발송을 중단했습니다.",
      {
        code: "DISPATCH_RESULT_UNKNOWN",
        status: 503,
        retryable: false,
        requestId: response?.headers?.get?.("x-request-id") || ""
      }
    );
  }
  const originalMessage = readRawMessage(null, data) || `HTTP ${response?.status || 0}`;
  return toAppError(
    { message: originalMessage, status: response?.status || 0 },
    { response, data, fallback }
  );
}
